/**
 * useCreditBurn — floating "-N cr" bubble that rises from a click point.
 *
 * Usage:
 *   const { fire, render } = useCreditBurn();
 *   return (
 *     <div style={{ position: 'relative' }}>
 *       {render()}
 *       <button onClick={(e) => fire(e, 5)}>Draft</button>
 *     </div>
 *   );
 *
 * Relies on `.credit-burn` CSS (already in index.css) for the animation.
 * The parent MUST be `position: relative` for the bubble to anchor.
 */
import { useRef, useState, type MouseEvent, type ReactNode } from 'react';

interface Burst {
  id: number;
  x: number;
  y: number;
  cost: number;
}

export function useCreditBurn() {
  const [bursts, setBursts] = useState<Burst[]>([]);
  const counter = useRef(0);

  const fire = (e: MouseEvent<HTMLElement>, cost: number) => {
    const target = e.currentTarget;
    const rect = target.getBoundingClientRect();
    const offsetParent = target.offsetParent as HTMLElement | null;
    const parentRect = offsetParent?.getBoundingClientRect() ?? { top: 0, left: 0 };
    const id = ++counter.current;
    const x = rect.left - parentRect.left + rect.width / 2;
    const y = rect.top - parentRect.top;
    setBursts((prev) => [...prev, { id, x, y, cost }]);
    // GC after animation ends (1.1s in CSS)
    window.setTimeout(() => {
      setBursts((prev) => prev.filter((b) => b.id !== id));
    }, 1200);
  };

  const render = (): ReactNode => bursts.map((b) => (
    <div
      key={b.id}
      className="credit-burn"
      style={{ left: b.x, top: b.y, transform: 'translateX(-50%)' }}
    >
      −{b.cost} cr
    </div>
  ));

  return { fire, render };
}
